import {captureException} from "@sentry/react";
import ErrorBoundary from "components/Error/errorBoundary";
import React, {Fragment, useCallback, useEffect, useMemo, useRef} from 'react';
import {FormProvider, useForm} from "react-hook-form";
import {useHistory} from "react-router-dom";
import Field from "v4/components/form/Field/Field";
import QuoteLines from "v4/components/form/Field/components/QuoteLines/QuotesLines";
import Loader from "v4/components/ui/Loader/Loader";
import PreventRedirect from "v4/components/utils/PreventRedirect/PreventRedirect";
import {QUOTE_LINE_TYPE} from "v4/data/fieldTypes";
import {FORM_ENTITY_USER_EVENT} from "v4/data/userEvents";
import useFetch from "v4/hooks/useFetch";
import useNotification from "v4/hooks/useNotification";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import FormEntityLayoutFormActions
    from "v4/layouts/FormEntityLayout/components/FormEntityLayoutForm/components/FormEntityLayoutFormActions/FormEntityLayoutFormActions";
import FormEntityFieldLabel
    from "v4/layouts/FormEntityLayout/components/FormEntityLayoutForm/components/FormEntityFieldLabel/FormEntityFieldLabel";
import 'v4/layouts/FormEntityLayout/components/FormEntityLayoutForm/FormEntityLayoutForm.scss';
import EntityLoop from "v4/layouts/partials/EntityLoop/EntityLoop";
import {generateUrl} from "v4/services/api.service";
import {userEventsService} from "v4/services/userEventsService";
import {trackQuoteAttributes} from "v4/services/debug/quoteAttributesSentry";
import {randomId, sanitizeRichText} from "v4/utils";
import ProductCustomizeVoter from "v4/voters/productCustomizeVoter";
import {createFormData} from "v4/layouts/FormEntityLayout/components/FormEntityLayoutForm/formEntityLayoutForm.utils";

export default function FormEntityLayoutForm({
                                                 inputsGroups,
                                                 submitRouteName,
                                                 additionalButtons,
                                                 whenSubmitted,
                                                 fetchFormParameters,
                                                 onBack,
                                                 shouldWarnOnChangePage
                                             }) {
    const {t} = usePolTranslation();
    const history = useHistory();
    const {canAccessProductAttributes} = ProductCustomizeVoter();
    const [{data, isLoading, isError, errorData, isFinished}, fetchSave] = useFetch();

    const [
        formMetadata,
        allInputs
    ] = useMemo(() => ([
        inputsGroups.metadata,
        Object.values(inputsGroups.results).map(({properties}) => properties).flat()
    ]), [inputsGroups]);

    const methods = useForm({
        mode: 'onChange',
        defaultValues: getInitialValues(allInputs),
    });

    useEffect(() => {
        const quoteLines = methods.getValues().quoteLines;
        if (Array.isArray(quoteLines)) {
            trackQuoteAttributes('load', quoteLines, {submitRouteName});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {addErrorNotification} = useNotification();
    const onSubmit = async data => {
        if (methods.formState.isDirty || shouldWarnOnChangePage) {
            const {taskId, quoteId, ...restParameters} = fetchFormParameters ?? {};
            const parameters = {};
            if (taskId) data['tasks'] = [{externalValue: taskId}];
            if (quoteId) data['quotes'] = [{externalValue: quoteId}];
            if (restParameters) {
                Object.keys(fetchFormParameters)
                    .forEach(key => parameters[key] = fetchFormParameters[key]);
            }

            // Force null value of hidden fields by conditional fields
            allInputs.forEach(input => {
                const {
                    'data-condition-field': conditionField,
                    'data-condition-value': conditionFieldAllowedValues,
                } = input?.attr ?? {};

                const conditionFieldValue = data[conditionField];

                if (!conditionField || !conditionFieldValue || [conditionFieldAllowedValues].flat().includes(conditionFieldValue)) return;

                data[input.name] = null;
            });

            const {
                alteredData,
                errors = []
            } = await userEventsService([FORM_ENTITY_USER_EVENT, `beforesave:${submitRouteName}`].filter(Boolean), data);
            if (errors?.length > 0) {
                errors.forEach(error => addErrorNotification(error));
                return;
            }

            const dataToSave = alteredData ?? data;
            if (dataToSave?.quoteLines) {
                trackQuoteAttributes('save:beforeSerialize', dataToSave.quoteLines, {submitRouteName});
            }

            fetchSave({
                url: Object.keys(parameters).length > 0
                    ? generateUrl(submitRouteName, parameters)
                    : generateUrl(submitRouteName),
                config: {
                    method: 'POST',
                    body: createFormData(dataToSave, allInputs, canAccessProductAttributes()),
                }
            })
        } else {
            history.goBack();
        }
    };

    useEffect(() => {
        if (isFinished && !isError && whenSubmitted) {
            whenSubmitted(history, data, methods);
            if (data?.isProspectRefreshNeeded) {
                history.replace({
                    state: {
                        hasToRefresh: true,
                        refreshProspectView: true,
                    }
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isFinished, isError, history, whenSubmitted]);

    useEffect(() => {
        if (isError && errorData?.errors?.length > 0) {
            const existingKeys = Object.keys(methods.getValues());
            errorData.errors.forEach(({key, messages}) => {
                if (!existingKeys.includes(key)) {
                    captureException(new Error(`The key ${key} does not exist in the form`), {
                        extra: {existingKeys, errorData}
                    });
                    return;
                }
                if (messages.length > 0) {
                    methods.setError(key, {type: 'invalidForm', message: messages.map(m => t(m)).join(' ')});
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, errorData]);

    const formPrefix = useRef(randomId());

    const AdditionalButtons = additionalButtons;
    const entityLoopChildrenRender = useCallback((input, {id: entityId}, showLabel) => (
        <Fragment key={input.name ?? input.key}>
            {input.entry_type === QUOTE_LINE_TYPE
                // quoteLines is driven by useFieldArray: Field's useController would register its root and break field array ops (PV-1789)
                ? <QuoteLines name={input.name ?? input.key} {...input}/>
                : <Field name={input.name ?? input.key} {...input} prefix={formPrefix.current} entityId={entityId} formMetadata={formMetadata}/>}
            {showLabel && (
                <FormEntityFieldLabel name={input.name ?? input.key}
                                      htmlFor={formPrefix.current + (input.name ?? input.key)}>
                    {t(input.label)}
                </FormEntityFieldLabel>
            )}
        </Fragment>
    ), [formMetadata, t]);

    return (
        <ErrorBoundary>
            {/* Demande confirmation à l'utilisateur avant de changer de page */}
            <PreventRedirect
                when={!methods.formState.isSubmitSuccessful && (shouldWarnOnChangePage || methods.formState.isDirty)}
                message={t('customer_confirm_leave_form_alert')}/>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}
                      className={`form form__scrollable form__edit--view form-v4`}>
                    {isLoading && (
                        <div className="form-v4__loader">
                            <Loader message="Enregistrement..."/>
                        </div>
                    )}
                    <FormEntityLayoutFormActions onBack={onBack}
                                                 entityId={fetchFormParameters?.id}
                                                 isSaveDisabled={methods.formState.isSubmitting}>
                        {AdditionalButtons && <AdditionalButtons/>}
                    </FormEntityLayoutFormActions>
                    <div className="input-field-group-wrapper">
                        <EntityLoop inputsGroups={inputsGroups}
                                    groupClassName="input-field-group"
                                    childrenClassName="input-field-wrapper">
                            {entityLoopChildrenRender}
                        </EntityLoop>
                    </div>
                </form>
            </FormProvider>
        </ErrorBoundary>
    )
}

function getInitialValues(values) {
    const initialValues = {};
    values.forEach(({type, name, key, value, attr}) => {
        if (type === 'ChoiceType' && value?.id) {
            return initialValues[name ?? key] = value.id;
        }
        if (type === 'ChoiceType') {
            return initialValues[name ?? key] = value ?? [];
        }
        if (type === 'TextareaType' && attr?.["rich-text-editor"]) {
            return initialValues[name ?? key] = sanitizeRichText(value);
        }

        return initialValues[name ?? key] = value ?? '';
    })
    return initialValues;
}


